<script setup lang="ts">
import { computed } from 'vue'

import { getClasses } from '~/js/composables/classes'
import { Analytics } from '~/js/interfaces/analytics'

interface Props {
    content?: string,
    disabled?: boolean
    ga?: Analytics | null,
    type?: 'a' | 'button' | 'div' | 'submit',
    theme?: string | null,
    url?: string | null,
    utilities?: string
}

const props = withDefaults(defineProps<Props>(), {
    content: '',
    disabled: false,
    type: 'button',
    url: null,
    utilities: '',
    variant: 'small',
})

const classes = {
    _default: {
        _default: 'inline-flex items-center justify-center cursor-pointer',
        size: 'w-fit',
        display: 'rounded-lg',
        spacing: 'px-2 py-1',
        text: 'text-sm font-primary',
        underline: 'underline underline-offset-6',
        hover: 'hover:bg-neutral-900 hover:text-white hover:no-underline',
        focus: 'focus-visible:ring-2 focus-visible:ring-offset-white dark:ring-offset-black focus-visible:ring-offset-2 focus-visible:outline-none',
        transition: 'transition-colors duration-200 transform-gpu ease-in-out',
    },
    light: {
        color: 'text-neutral-900',
        focus: 'focus-visible:ring-indigo',
    },
    dark: {
        color: 'dark:text-white',
        focus: 'dark:focus-visible:ring-yellow',
    },
}

const getType = () => {
    return props.type == 'submit' ? 'button' : props.type
}

const attributes = computed(() => {
    if (props.type === 'div') {
        return {}; // No attributes for div
    }

    return {
        title: props.ga?.label ?? props.content,
        'data-tag-category': props.ga?.category ?? '',
        'data-tag-action': props.ga?.action ?? '',
        'data-tag-label': props.ga?.label ?? '',
    }
})

</script>

<template>
    <component
        :is="getType()"
        :class="[
            getClasses(classes._default), 
            getClasses(classes.light), 
            getClasses(classes.dark), 
            utilities
        ]"
        :href="url"
        :type="type"
        v-bind="attributes"
    >
        {{ content }}
    </component>
</template>